import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';
import {Box} from '@material-ui/core';

import {getDirectionStyles} from '../../../../../utils/ThemeUtils';
import useLocalization from '../../../../../hooks/useLocalization';
import StatisticsPanel from '../../../../sidebar/StatisticsPanel';
import StatisticsCard from '../../../../sidebar/StatisticsCard';
import useGameXpHighlightsData from './useGameXpHighlightsData';
import {HighlightsDatapoint} from '../../../../../reducers/fortnite-game/gameXpHighlights.reducer';
import StatisticsCardContent from '../../../../sidebar/StatisticsCardContent';
import HighlightTrend from './HighlightTrend';
import SparkLineGraph from './SparkLineGraph';
import DataUnavailableTooltip from '../../../../sidebar/DataUnavailableTooltip';
import {GameXpMetric} from '../../../../../../shared/types/FortniteGames';
import useOrdinalNumberFormatter from '../hooks/useOrdinalNumberFormatter';

const StartAdornment = styled.span`
    display: inline-block;

    margin: ${p =>
        getDirectionStyles(p.theme.muiTheme.direction)(
            p.theme.muiTheme.spacing(0, 1, 0, 0),
            p.theme.muiTheme.spacing(0, 0, 0, 1)
        )};
`;

const CODES = {
    [GameXpMetric.PLAYERS]: {
        title: 'epic.affiliate.fortnite_games.xp.sidebar.active_players',
        description: 'epic.affiliate.fortnite_games.xp.sidebar.active_players.description'
    },
    [GameXpMetric.PERCENTILE]: {
        title: 'epic.affiliate.fortnite_games.xp.sidebar.xp_per_minute',
        description: 'epic.affiliate.fortnite_games.xp.sidebar.xp_per_minute.description',
        tooltip: 'epic.affiliate.fortnite_games.xp.sidebar.xp_per_minute.tooltip'
    },
    [GameXpMetric.ACTIVATIONS]: {
        title: 'epic.affiliate.fortnite_games.xp.sidebar.total_activations',
        description: 'epic.affiliate.fortnite_games.xp.sidebar.total_activations.description'
    },
    title: 'epic.affiliate.fortnite_games.xp.sidebar.title',
    tooltip: 'epic.affiliate.fortnite_games.xp.sidebar.tooltip',
    dataUnavailableTitle: 'epic.affiliate.fortnite_games.sidebar.data_unavailable_title',
    dataUnavailableDescription:
        'epic.affiliate.fortnite_games.sidebar.data_unavailable_description',
    percentileDescription: 'epic.affiliate.fortnite_games.xp.sidebar.percentile'
};

interface HighlightsPanelProps {
    loading: boolean;
    islandCode?: string;
}

interface HighlightsPanelData extends HighlightsDatapoint {
    loading: boolean;
    error?: string;
}

const HighlightsPanel: FC<HighlightsPanelProps> = ({islandCode, loading: gamesLoading}) => {
    const getMessage = useLocalization();
    const formatOrdinal = useOrdinalNumberFormatter();

    const {data, loading, error} = useGameXpHighlightsData(islandCode);
    const {percentile} = data;

    const percentileStat = percentile && percentile.stats && percentile.stats.slice(-1)[0];

    const percentileLine = percentileStat
        ? `${formatOrdinal(percentileStat)} ${getMessage(CODES.percentileDescription)}`
        : percentileStat;

    const stats = Object.values(GameXpMetric).reduce((acc, stat) => {
        acc[stat] = {...data[stat], loading, error};

        return acc;
        // eslint-disable-next-line
    }, {} as HighlightsPanelData);

    const getStartAdornment = (adornment: ReactNode): ReactNode => (
        <StartAdornment>{adornment}</StartAdornment>
    );
    const getEndAdornment = (stat: GameXpMetric): ReactNode =>
        stat === GameXpMetric.PERCENTILE ? '%' : '';

    return (
        <StatisticsPanel title={getMessage(CODES.title)} tooltip={getMessage(CODES.tooltip)}>
            <Box mt={-2}>
                {Object.entries(stats).map(([stat, {stats, value, trend = 0, loading}]) => (
                    <StatisticsCard
                        key={stat}
                        title={getMessage(CODES[stat].title)}
                        titleTooltip={getMessage(CODES[stat].tooltip)}
                        value={
                            <StatisticsCardContent
                                value={stat === GameXpMetric.PERCENTILE ? percentileLine : value}
                                title={getMessage(CODES.dataUnavailableTitle)}
                                tooltip={
                                    !value && (
                                        <DataUnavailableTooltip
                                            title={getMessage(CODES.dataUnavailableTitle)}
                                            description={getMessage(
                                                CODES.dataUnavailableDescription
                                            )}
                                        />
                                    )
                                }
                            />
                        }
                        trend={
                            <HighlightTrend
                                startAdornmentPositive={getStartAdornment('+')}
                                startAdornmentNegative={getStartAdornment('-')}
                                value={trend}
                                endAdornment={getEndAdornment(stat as GameXpMetric)}
                            />
                        }
                        description={getMessage(CODES[stat].description)}
                        isLoading={gamesLoading || loading}>
                        {stats && !!stats.length && <SparkLineGraph stats={stats} trend={trend} />}
                    </StatisticsCard>
                ))}
            </Box>
        </StatisticsPanel>
    );
};

export default HighlightsPanel;
